<script setup lang="ts">
import type { DocumentNode } from '@marvr/storyblok-rich-text-types'
import { getRules } from 'core/services/rules'
import { useMask } from 'core/services/mask'

const props = defineProps<{
  modelValue: boolean
  title: string
  text: DocumentNode
  fields: FieldContent[]
  gdprText: DocumentNode
  submitButton: StoryBlokButton[]
  closeButton: StoryBlokButton[]
  successTitle: string
  successText: DocumentNode
  successButton: StoryBlokButton[]
}>()

const emit = defineEmits(['update:modelValue'])
const initialProduct = defineModel('initialProduct')
const products = props.fields?.find((field: any) => field?.name === 'product')?.options

const { public: { marketingCloudInstance, storyblokPortal } } = useRuntimeConfig()
const { locale } = useAppConfig()
const rules = getRules()
const isFormSuccess = ref(false)
const isLoading = ref(false)
const { phone } = useMask()

async function onSubmit({ phone, product }: { phone: string; product: string }) {
  isLoading.value = true

  const chosenProduct = products?.find((item: any) => item.code === product)

  const dispatcherPayload = {
    phone,
    product: chosenProduct?.label,
    marketingCloudInstance,
    storyblokPortal,
    locale: locale.code,
  }

  const data = await fetchWebApi('/shared-message-dispatcher/cta-product-dispatch', {
    method: 'post',
    body: { dispatcherPayload },
  })

  isLoading.value = false

  if (data?.success) isFormSuccess.value = true
}

function closeModal() {
  isFormSuccess.value = false
  emit('update:modelValue', false)
}
</script>

<template>
  <BaseModal v-bind="props" @close="closeModal">
    <template #header>
      <h3 v-if="!isFormSuccess">
        {{ title }}
      </h3>
      <h3 v-if="isFormSuccess" class="text-success">
        {{ successTitle }}
      </h3>
    </template>
    <template #content>
      <div v-if="!isFormSuccess" class="flex flex-col gap-6 mt-6">
        <StoryblokRichText :document="text" />
        <FormSimple :fields="fields" :initial-values="initialProduct && { product: initialProduct }" @submit="onSubmit">
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-4 sm:flex-row">
              <FormFieldText name="phone" :rules="rules.phone" inputmode="tel" :mask="phone">
                <template #start>
                  <div class="min-w-90px flex">
                    <FormFieldDropdown name="phonePrefix" hide-border class="text-black" />
                    <div class="flex items-center">
                      <div class="border-r-1 border-gray h-3/6" />
                    </div>
                  </div>
                </template>
              </FormFieldText>
              <FormFieldDropdown name="product" class="text-black sm:w-1/2" />
            </div>
            <div class="flex gap-2">
              <AppIcon class="text-gray-dark" size="18" type="far" icon="lock-alt" />
              <StoryblokRichText :document="gdprText" class="text-gray-dark text-sm" />
            </div>
            <div class="flex flex-col w-full gap-4 sm:flex-row">
              <BaseButton submit v-bind="submitButton[0]" :loading="isLoading" class="w-full sm:w-1/2" />
              <div class="w-full sm:w-1/2">
                <StoryblokButton v-bind="closeButton[0]" @click="emit('update:modelValue', false)" />
              </div>
            </div>
          </div>
        </FormSimple>
      </div>
      <div v-if="isFormSuccess" class="flex flex-col gap-6 mt-6">
        <StoryblokRichText :document="successText" />
        <StoryblokButton v-bind="successButton[0]" @click="closeModal" />
      </div>
    </template>
  </BaseModal>
</template>
